import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
  useLayoutEffect,
} from 'react';
import './styles.scss';
import TextEditorItem from '@components/textContainer/TextEditorItem';
import { NoDataWithName } from '@components';
import { v4 as uuidv4 } from 'uuid';
import ActionButton from './ActionButton';
import classNames from 'classnames';
import pick from 'lodash/pick';
import ResizableColumn from '@components/textContainer/ResizableColumn';
import {
  calculateColumnsWidthRatioOnAdd,
  getAverageColumnsWidthRatio,
  getMaxColumnIndex,
} from '@components/textContainer/helper';

export function generateEmptyItem(rowIndex = 0, colIndex = 0) {
  return {
    value: '',
    key: uuidv4(),
    rowIndex,
    colIndex,
  };
}

/**
 * TextContainer
 * @param editable
 * @param initialValues: [{ rowIndex: number, coleIndex: number, value: { put rich text editor value here }, key: string }]
 * @param initialColumnRatio: number[]
 * @param ref: { setData, getData }
 * @returns {JSX.Element}
 * @constructor
 */
function TextContainer(
  { editable = false, initialValues, initialColumnRatio },
  ref,
) {
  const [activeKey, setActiveKey] = useState(null);
  const [data, setData] = useState([]);
  const [refreshCount, setRefreshCount] = useState(0);
  const [columnsWidth, setColumnWidth] = useState([]);
  // const columnsWidthInit = useRef(false);
  const observeClassName = useRef(`js-observe-${crypto.randomUUID()}`);
  // const containerWidth = useRef(0);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (initialValues?.length && initialColumnRatio?.length) {
      const initData = JSON.parse(JSON.stringify(initialValues));
      setData(initData);
      setColumnWidth(initialColumnRatio);
      setRefreshCount((i) => i + 1);
    }
  }, [initialValues, initialColumnRatio]);
  const enterEditMode = useCallback(
    (key) => {
      if (editable) {
        if (activeKey) {
          setData((data) => {
            const targetData = data.find((i) => i.key === activeKey);
            if (targetData && targetData.result) {
              targetData.value = targetData.result;
              Reflect.deleteProperty(targetData, 'result');
            }
            return [...data];
          });
        }
        setActiveKey(key);
      }
    },
    [editable, activeKey],
  );
  const onItemChange = (key, content) => {
    // setActiveKey(null);
    setData((data) => {
      const targetData = data.find((i) => i.key === key);
      if (targetData) {
        // store changes in result field temporarily, avoiding mutating value directly
        targetData.result = content;
      }
      return [...data];
    });
  };
  const { colIndexMax, rowIndexMax, rowHeight } = useMemo(() => {
    const rowIndexMax = Math.max(...data.map((i) => i.rowIndex), 0);
    const colIndexMax = Math.max(...data.map((i) => i.colIndex), 0);
    const rowHeight = `${100 / (rowIndexMax + 1)}%`;

    return {
      rowIndexMax,
      colIndexMax,
      rowHeight,
    };
  }, [data.length]);

  const addRow = useCallback(() => {
    setData((previousData) => {
      const nextData = [...previousData];
      if (data.length === 0) {
        nextData.push(generateEmptyItem(0, 0));
      } else {
        for (let i = 0; i <= colIndexMax; i++) {
          nextData.push(generateEmptyItem(rowIndexMax + 1, i));
        }
      }
      return nextData;
    });
  }, [colIndexMax, rowIndexMax, data.length]);
  const addColumn = useCallback(() => {
    setData((previousData) => {
      const nextData = [...previousData];
      if (data.length === 0) {
        nextData.push(generateEmptyItem(0, 0));
      } else {
        for (let i = 0; i <= rowIndexMax; i++) {
          nextData.push(generateEmptyItem(i, colIndexMax + 1));
        }
      }
      return nextData;
    });
    updateColumnsWidth('add');
  }, [colIndexMax, rowIndexMax, data.length]);
  const deleteCurrentRow = useCallback(() => {
    if (!activeKey) {
      return;
    }
    setData((previousData) => {
      const targetItem = previousData.find((i) => i.key === activeKey);
      if (targetItem) {
        const { rowIndex } = targetItem;
        const nextData = [...previousData].filter(
          (i) => i.rowIndex !== rowIndex,
        );
        nextData.forEach((i) => {
          if (i.rowIndex > rowIndex) {
            i.rowIndex--;
          }
        });
        return nextData;
      }
      return previousData;
    });
  }, [activeKey]);
  const deleteCurrentColumn = useCallback(() => {
    setData((previousData) => {
      const targetItem = previousData.find((i) => i.key === activeKey);
      if (targetItem) {
        const { colIndex } = targetItem;
        const nextData = [...previousData].filter(
          (i) => i.colIndex !== colIndex,
        );
        nextData.forEach((i) => {
          if (i.colIndex > colIndex) {
            i.colIndex--;
          }
        });
        // eslint-disable-next-line no-new
        new Promise(() => {
          updateColumnsWidth('delete', colIndex);
        });
        return nextData;
      }
      return previousData;
    });
  }, [activeKey]);

  const updateColumnsWidth = (action, colIndex) => {
    if (action === 'add') {
      setColumnWidth((previousColumnsWidth) =>
        calculateColumnsWidthRatioOnAdd(previousColumnsWidth),
      );
    }
    if (action === 'delete') {
      setColumnWidth((previousData) => {
        if (previousData.length === 2) {
          return [1];
        }
        let result = [...previousData].filter((_, index) => index !== colIndex);
        const scaleRatio = 1 / result.reduce((cur, sum) => cur + sum, 0);
        result = result.map((i) => i * scaleRatio);
        return result;
      });
    }
  };

  const onColumnResize = (width, index) => {
    setColumnWidth((previousData) => {
      const nextData = [...previousData];
      const nextWidthRatio = width / containerWidth;
      // min column width is 12.5%
      if (nextWidthRatio < 0.125) {
        return previousData;
      }
      const previousWidthRatio = nextData[index];
      const changedRatio = nextWidthRatio - previousWidthRatio;
      nextData[index] = nextWidthRatio;
      if (nextData[index + 1]) {
        nextData[index + 1] -= changedRatio;
        if (nextData[index + 1] < 0.125) {
          return previousData;
        }
      }
      if (nextData.reduce((cur, sum) => cur + sum, 0) > 1) {
        return previousData;
      }
      return nextData;
    });
  };

  useImperativeHandle(
    ref,
    () => ({
      getData: () => ({
        contents: data.map((i) => ({
          ...pick(i, ['colIndex', 'rowIndex', 'key']),
          value: i.result || i.value,
        })),
        columnsWidthRatio: columnsWidth.map((i) => Number(i.toFixed(8))),
      }),
      setData: (nextData, nextColumnsWidthRatio) => {
        if (!nextData) {
          return;
        }
        setData(JSON.parse(JSON.stringify(nextData)));
        const colCount = getMaxColumnIndex(nextData) + 1;
        setColumnWidth(
          Array.isArray(nextColumnsWidthRatio) &&
            colCount === nextColumnsWidthRatio?.length
            ? nextColumnsWidthRatio
            : getAverageColumnsWidthRatio(nextData),
        );
        setRefreshCount((i) => i + 1);
      },
    }),
    [data, columnsWidth],
  );

  useEffect(() => {
    if (editable && data.length === 0 && !initialValues?.length) {
      const newItem = generateEmptyItem(0, 0);
      setData([newItem]);
      setColumnWidth([1]);
      setTimeout(() => {
        setActiveKey(newItem.key);
      }, 0);
    } else if (!editable) {
      setActiveKey(null);
    }
  }, [editable]);

  useLayoutEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // const cr = entry.contentRect;
        // containerWidth.current = cr.width;
        setContainerWidth(entry.contentRect.width);
      }
    });
    const containerNode = document.querySelector(
      `.${observeClassName.current}`,
    );
    if (containerNode) {
      observer.observe(containerNode);
    }
    return () => {
      observer.disconnect();
    };
  }, []);

  const dataColumns = useMemo(() => {
    const result = [];
    new Array(colIndexMax + 1).fill([]);
    for (let index = 0; index <= colIndexMax; index++) {
      result[index] = data.filter((item) => item.colIndex === index);
    }

    return result;
  }, [data]);

  return (
    <div className="text-container">
      {editable ? (
        <ActionButton
          add={addColumn}
          deleteCurrent={deleteCurrentColumn}
          type="Column"
          style={{ textAlign: 'right' }}
          addBtnDisabled={colIndexMax >= 7}
        />
      ) : null}
      <div
        className={classNames(
          `text-container-layout ${observeClassName.current}`,
          { editing: editable },
        )}
        style={{
          // gridTemplateRows: `repeat(${rowIndexMax + 1}, ${
          //   100 / (rowIndexMax + 1)
          // }%)`,
          // gridTemplateRows: `fit-content(100%)`,
          gridTemplateColumns: columnsWidth.map((i) => `${i * 100}%`).join(' '),
        }}
      >
        {data.length ? (
          data.map((item, index) => (
            // <ResizableColumn
            //   width={containerWidth * columnsWidth[index]}
            //   onWidthChange={onColumnResize}
            //   index={index}
            //   disabled={!editable}
            // >
            //   {col.map((item) => (
            //
            //   ))}
            // </ResizableColumn>
            <TextEditorItem
              value={item.value}
              colIndex={item.colIndex}
              rowIndex={item.rowIndex}
              editable={editable && activeKey === item.key}
              itemKey={item.key}
              key={item.key}
              onClick={enterEditMode}
              onChange={onItemChange}
              refreshCount={refreshCount}
              width={containerWidth * columnsWidth[item.colIndex]}
              onWidthChange={onColumnResize}
            />
          ))
        ) : (
          <div className="text-container-empty">
            <NoDataWithName text='Select "Edit Text" button to get started' />
          </div>
        )}
      </div>
      {editable ? (
        <ActionButton
          add={addRow}
          deleteCurrent={deleteCurrentRow}
          type="Row"
          // addBtnDisabled={rowIndexMax >= 3}
        />
      ) : null}
    </div>
  );
}

export default forwardRef(TextContainer);
